import { NxErrorModule } from "@allianz/ng-aquila/base";
import { NxButtonModule } from "@allianz/ng-aquila/button";
import { NxCheckboxModule } from "@allianz/ng-aquila/checkbox";
import { NxDropdownModule } from "@allianz/ng-aquila/dropdown";
import { NxFormfieldModule } from "@allianz/ng-aquila/formfield";
import { NxGridModule } from "@allianz/ng-aquila/grid";
import { NxIconModule } from "@allianz/ng-aquila/icon";
import { NxInputModule } from "@allianz/ng-aquila/input";
import { NxLinkModule } from "@allianz/ng-aquila/link";
import { NxMessageModule } from "@allianz/ng-aquila/message";
import { NxPopoverModule } from "@allianz/ng-aquila/popover";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { CommonModalComponent } from "@modules/modals/common-modal/common-modal.component";
import { TermsOfUseModalComponent } from "@modules/modals/terms-of-use-modal/terms-of-use-modal.component";
import { TranslateModule } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import { CustomValidators } from "@shared/classes/custom-validator";
import { firstTimeSuccessModalData } from "@shared/data/modalData";
import { NxPasswordTouchToggleDirective } from "@shared/directives/nx-password-touch-toggle.directive";
import { Observable } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { ModalService } from "src/app/shared/services/modal.service";
import { GET_SECRET_QUESTION_LIST } from "src/app/store/login/login.action";
import { LoginState } from "src/app/store/login/login.state";
import {
  SET_LOGGED_IN_STATE,
  SUBMIT_FIRST_TIME_LOGIN,
} from "src/app/store/user/user.action";
import { UserState } from "src/app/store/user/user.state";

import { AuthSideHeaderComponent } from "../auth-side-header/auth-side-header.component";

@Component({
  selector: "app-first-time-login",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxCheckboxModule,
    NxDropdownModule,
    NxErrorModule,
    NxFormfieldModule,
    NxGridModule,
    NxIconModule,
    NxInputModule,
    NxLinkModule,
    NxMessageModule,
    NxPopoverModule,
    TranslateModule,
    AuthSideHeaderComponent,
    NxPasswordTouchToggleDirective,
  ],
  templateUrl: "./first-time-login.component.html",
  styleUrl: "./first-time-login.component.scss",
})
export class FirstTimeLoginComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Select(UserState.getFirstTimeLoginState)
  protected firstTimeLoginState$;
  @Select(LoginState.getSecretQuestionList)
  protected secretQuestionList$: Observable<any[]>;

  protected firstTimeLoginForm!: FormGroup;
  protected firstTimeLoginErrorMsg: string | null = null;
  protected success: boolean = false;

  protected get confirmPasswordErrorType() {
    const errors = this.firstTimeLoginForm.controls["confirmPassword"].errors;
    return errors ? this.getErrorType(errors) : null;
  }

  protected get confirmSecretAnswerErrorType() {
    const errors =
      this.firstTimeLoginForm.controls["confirmSecretAnswer"].errors;
    return errors ? this.getErrorType(errors) : null;
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly router: Router,
    private readonly modalService: ModalService,
    private readonly crossAppNavigationService: CrossAppNavigationService,
  ) {}

  ngOnInit() {
    this.formInit();
    this.store.dispatch(new GET_SECRET_QUESTION_LIST());
    this.onChangePassword();
    this.onChangeConfirmPassword();
    this.onChangeSecretAnswer();
    this.onChangeConfirmSecretAnswer();
    window.scroll(0, 0);
  }

  private formInit() {
    const user = this.store.selectSnapshot<any>(
      (state) => state.UserState.user,
    );

    this.firstTimeLoginForm = this.fb.group({
      email: [
        user?.email ?? "",
        Validators.compose([Validators.required, Validators.email]),
      ],
      name: [user?.preferName ?? "", Validators.required],
      mobileNumber: [
        user?.mobileNo ?? "",
        Validators.compose([
          Validators.required,
          Validators.pattern("(^(0|1|2|3|4|5|6|7|8|9){6,20}$)"),
          Validators.pattern("^[^\\.]*$"),
        ]),
      ],
      password: [
        "",
        Validators.compose([
          Validators.required,
          CustomValidators.patternValidator(/^.{15,20}$/, {
            minAndMaxNum: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-zA-Z])(?=.*[0-9])/, {
            hasAlphanumeric: true,
          }),
          CustomValidators.patternValidator(/^(?=.*[a-z])(?=.*[A-Z])/, {
            uppercaseAndLowercase: true,
          }),
          CustomValidators.patternValidator(/[@#$^%&*!]/, {
            hasSpecialCharacters: true,
          }),
        ]),
      ],
      confirmPassword: ["", Validators.required],
      secretQuestion: [user?.secretQuestion ?? "", Validators.required],
      secretAnswer: ["", Validators.required],
      confirmSecretAnswer: ["", Validators.required],
      acknowledgeChecked: [false, Validators.required],
    });
  }

  protected onSubmit() {
    if (
      this.firstTimeLoginForm.controls["password"].value !==
      this.firstTimeLoginForm.controls["confirmPassword"].value
    ) {
      this.firstTimeLoginForm.controls["confirmPassword"].setErrors({
        passwordMisMatch: true,
      });
      return;
    }

    if (
      this.firstTimeLoginForm.controls["secretAnswer"].value !==
      this.firstTimeLoginForm.controls["confirmSecretAnswer"].value
    ) {
      this.firstTimeLoginForm.controls["confirmSecretAnswer"].setErrors({
        secretAnswerMisMatch: true,
      });
      return;
    }

    const userState = this.store.selectSnapshot<any>(
      (state) => state.UserState,
    );
    const payload = {
      userId: userState.tempUserId,
      currentPassword: userState.tempPass,
      newPassword: this.firstTimeLoginForm.get("password").value,
      confirmedNewPassword:
        this.firstTimeLoginForm.get("confirmPassword").value,
      secretQuestion: this.firstTimeLoginForm.get("secretQuestion").value,
      secretAnswer: this.firstTimeLoginForm.get("secretAnswer").value,
      preferName: this.firstTimeLoginForm.get("name").value,
      mobileNo: this.firstTimeLoginForm.get("mobileNumber").value,
      email: this.firstTimeLoginForm.get("email").value,
      confirmedSecretAnswer: this.firstTimeLoginForm.get("confirmSecretAnswer")
        .value,
      termsOfUse: true,
    };

    this.store
      .dispatch(new SUBMIT_FIRST_TIME_LOGIN(payload))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const state = this.store.selectSnapshot<any>(
          (snapshot) => snapshot.UserState,
        );
        if (
          state.firstTimeLoginState?.isFirstTimeLoginSuccess &&
          !state.firstTimeLoginState?.firstTimeLoginErrorMsg
        ) {
          this.success = true;
          this.firstTimeLoginErrorMsg = null;
          this.modalService.openDialogWithCallbackHandler(
            CommonModalComponent,
            firstTimeSuccessModalData,
            (result) => {
              if (result) {
                this.navigateToPostLoginDestination();
              }
            },
          );
          return;
        }

        this.success = false;
        this.firstTimeLoginErrorMsg =
          state.firstTimeLoginState?.firstTimeLoginErrorMsg ?? null;
      });
  }

  protected toggleTermOfUse() {
    this.modalService.openDialog(TermsOfUseModalComponent);
  }

  private navigateToPostLoginDestination() {
    this.store
      .dispatch(new SET_LOGGED_IN_STATE())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // TODO: Remove this once R2B is stable
        this.crossAppNavigationService.redirectToAlphaSatPath(
          "/home?token=TOKEN",
        );
      });
  }

  private onChangePassword() {
    this.firstTimeLoginForm
      .get("password")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe((password: string) => {
        this.resetConfirmNewPassword();
        const confirmPassword =
          this.firstTimeLoginForm.controls["confirmPassword"].value;
        if (password && confirmPassword) {
          this.checkPasswordMisMatch(password, confirmPassword);
        }
      });
  }

  private onChangeConfirmPassword() {
    this.firstTimeLoginForm
      .get("confirmPassword")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmPassword: string) => {
        const password = this.firstTimeLoginForm.controls["password"].value;
        if (password && confirmPassword) {
          this.checkPasswordMisMatch(password, confirmPassword);
        }
      });
  }

  private onChangeSecretAnswer() {
    this.firstTimeLoginForm
      .get("secretAnswer")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe((secretAnswer: string) => {
        this.resetConfirmSecretAnswer();
        const confirmSecretAnswer =
          this.firstTimeLoginForm.controls["confirmSecretAnswer"].value;
        if (secretAnswer && confirmSecretAnswer) {
          this.checkSecretAnswerMisMatch(secretAnswer, confirmSecretAnswer);
        }
      });
  }

  private onChangeConfirmSecretAnswer() {
    this.firstTimeLoginForm
      .get("confirmSecretAnswer")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmSecretAnswer: string) => {
        const secretAnswer =
          this.firstTimeLoginForm.controls["secretAnswer"].value;
        if (secretAnswer && confirmSecretAnswer) {
          this.checkSecretAnswerMisMatch(secretAnswer, confirmSecretAnswer);
        }
      });
  }

  private resetConfirmNewPassword() {
    this.firstTimeLoginForm.patchValue({
      confirmPassword: "",
    });
    this.firstTimeLoginForm.controls["confirmPassword"].reset();
  }

  private resetConfirmSecretAnswer() {
    this.firstTimeLoginForm.patchValue({
      confirmSecretAnswer: "",
    });
    this.firstTimeLoginForm.controls["confirmSecretAnswer"].reset();
  }

  private checkPasswordMisMatch(password: string, confirmPassword: string) {
    if (password !== confirmPassword) {
      this.firstTimeLoginForm.controls["confirmPassword"].setErrors({
        passwordMisMatch: true,
      });
    }
  }

  private checkSecretAnswerMisMatch(
    secretAnswer: string,
    confirmSecretAnswer: string,
  ) {
    if (secretAnswer !== confirmSecretAnswer) {
      this.firstTimeLoginForm.controls["confirmSecretAnswer"].setErrors({
        secretAnswerMisMatch: true,
      });
    }
  }

  private getErrorType(errors) {
    if (errors.passwordMisMatch) {
      return "passwordMisMatch";
    }
    if (errors.samePassword) {
      return "samePassword";
    }
    if (errors.secretAnswerMisMatch) {
      return "secretAnswerMisMatch";
    }
    if (errors.required) {
      return "required";
    }
    return null;
  }
}
